import { Children, useEffect, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  LEFT,
  MEDIUM_VIEWPORT_MEDIA_QUERY,
  RIGHT,
  SCROLL_DISTANCE,
} from "../constants";
import { useChildAccessibility, useMediaQuery, useScrollState } from "../hooks";
import { ScreenReaderInstructions } from "./ScreenReaderInstructions";
import CarouselButton from "./CarouselButton";
import { ListElement } from "./ListElement";
import { withTranslation } from "../../translation";
import styles from "../index.css";
import { useScrollItemIntoCenter } from "../../hooks/useScrollItemIntoCenter";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const CarouselWrapper = ({
  scrollSnapping,
  children,
  className,
  ariaLabel,
  formatTranslation,
  showControlsOnMobile = false,
  visualCategoriesDescription,
  selectedItemSelector,
  selectedItemDependency,
}) => {
  const listRef = useRef(null);
  const childCount = Children.count(children);
  const isNotMobileViewport = useMediaQuery(MEDIUM_VIEWPORT_MEDIA_QUERY);
  const { hasOverflow, isAtStart, isAtEnd, updateScrollState } = useScrollState(
    listRef,
    childCount,
  );
  const { handleChildAccessibility } = useChildAccessibility(
    listRef,
    isNotMobileViewport,
  );
  const scrollItemIntoCenter = useScrollItemIntoCenter(listRef);

  useEffect(() => {
    handleChildAccessibility();
  }, [handleChildAccessibility, hasOverflow]);

  useIsomorphicLayoutEffect(() => {
    if (!selectedItemSelector) return;

    const selected = listRef.current?.querySelector(selectedItemSelector);
    let rafId;

    if (selected) {
      scrollItemIntoCenter(selected);
      rafId = requestAnimationFrame(updateScrollState);
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    selectedItemSelector,
    selectedItemDependency,
    scrollItemIntoCenter,
    updateScrollState,
  ]);

  const scroll = (direction) => {
    if (!listRef.current) return;

    listRef.current.scrollBy({
      left: direction === LEFT ? -SCROLL_DISTANCE : SCROLL_DISTANCE,
      behavior: "smooth",
    });
  };

  const showCarousel =
    hasOverflow && (isNotMobileViewport || showControlsOnMobile);

  const hasHeaderContent = Boolean(visualCategoriesDescription);
  const buttonsWrapperClassName = classnames(styles.buttonsWrapper, {
    [styles.mobileControlsVisible]:
      showControlsOnMobile && !isNotMobileViewport,
  });

  const carouselControls = showCarousel ? (
    <div
      className={buttonsWrapperClassName}
      data-horizontal-scroll-list-controls="true"
    >
      <ScreenReaderInstructions formatTranslation={formatTranslation} />
      <CarouselButton
        direction={LEFT}
        scroll={scroll}
        disabled={!hasOverflow || isAtStart}
      />
      <CarouselButton
        direction={RIGHT}
        scroll={scroll}
        disabled={!hasOverflow || isAtEnd}
      />
    </div>
  ) : null;

  return (
    <div
      className={classnames(styles.listContainer, className)}
      role={showCarousel ? "region" : undefined}
      aria-label={showCarousel ? formatTranslation(ariaLabel) : undefined}
    >
      {hasHeaderContent ? (
        <div
          className={styles.visualCategoriesDescriptionAndArrows}
          data-horizontal-scroll-list-header="true"
        >
          {visualCategoriesDescription}
          {carouselControls}
        </div>
      ) : (
        <>{carouselControls}</>
      )}
      <ListElement
        listRef={listRef}
        scrollSnapping={scrollSnapping}
        showCarousel={showCarousel}
      >
        {children}
      </ListElement>
    </div>
  );
};

CarouselWrapper.propTypes = {
  scrollSnapping: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  formatTranslation: PropTypes.func.isRequired,
  showControlsOnMobile: PropTypes.bool,
  visualCategoriesDescription: PropTypes.node,
  selectedItemSelector: PropTypes.string,
  selectedItemDependency: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
};

export default withTranslation(CarouselWrapper);
